/**
 * AUTOMATION GALLERY — Template gallery (da wireframe "Template Gallery").
 * Cablata ai dati reali: tab e colori chip da `fetchAutomationCategories`, card
 * da `fetchAutomationTemplates` (AutomationDefinition con template=true), e il flusso
 * QUANDO/SE/ALLORA di ogni card da `summarizeAutomation` — la stessa resa del box "Riepilogo" del
 * builder, alimentata dallo stesso catalogo.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { PlusGlyph, ChevronDownGlyph, AutomationsGlyph } from '../../components/icons';
import { getEntityShortNameByFqn } from '../../entityRegistry/entityRegistry';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useToast } from '../../components/useToast';
import {
  fetchAutomationCategories,
  fetchAutomationConditionTypes,
  fetchAutomationObjectTypes,
  fetchAutomationTemplates,
  restoreAutomationTemplates,
} from './automationThunks';
import { SUMMARY_PLACEHOLDER, summarizeAutomation } from './automationSummary';
import AutomationSummaryTokens from './AutomationSummaryTokens';
import type { AutomationStatus } from './automationTypes';

/**
 * Stato -> variante del chip del design system (`STATUS_CHIP_COLOR` parla la
 * lingua di MUI). Stessa mappa dell'elenco automazioni.
 */
const STATUS_CHIP_TONE: Record<AutomationStatus, string> = {
  active: 'ok',
  draft: 'neutral',
  suspended: 'warn',
  archived: 'neutral',
};

/**
 * Tinta dell'emblema in base all'entita' su cui gira l'automazione. Le tre
 * riconosciute sono quelle del concept; per tutto il resto — comprese le entita'
 * dei plugin — resta il grigio neutro, che e' meglio di un colore inventato.
 */
function emblemTone(className: string): string {
  switch (getEntityShortNameByFqn(className)) {
    case 'Person': return 'contatto';
    case 'Quotation': return 'preventivo';
    case 'Business': return 'azienda';
    default: return '';
  }
}

export default function AutomationGallery() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const categories = useAppSelector((s) => s.automations.categories);
  const allTemplates = useAppSelector((s) => s.automations.templates);
  // I modelli di un tipo spento dalla pagina «Voci del CRM» (FILO-53) non si propongono.
  const isClassNameDisabled = useIsClassNameDisabled();
  const templates = useMemo(
    () => allTemplates.filter((tmpl) => !isClassNameDisabled(tmpl.className)),
    [allTemplates, isClassNameDisabled],
  );
  const objectTypes = useAppSelector((s) => s.automations.objectTypes);
  const conditionTypesByClassName = useAppSelector((s) => s.automations.conditionTypesByClassName);

  const toast = useToast();
  const { isOpen, message, options, requestConfirm, confirm, cancel } = useConfirmDialog();

  // Filtro categoria: 'all' (tab "tutti" sintetico) oppure un automationCategoryId reale.
  const [activeCategory, setActiveCategory] = useState<number | 'all'>('all');
  const [restoring, setRestoring] = useState(false);

  /**
   * Ripristino dei predefiniti: conferma con RIDIGITAZIONE, non con un semplice "sei sicuro?".
   * L'operazione elimina i template di sistema con TUTTE le versioni che ne sono state derivate,
   * e non si annulla — è la stessa asticella delle altre azioni irreversibili del CRM.
   *
   * Dopo, si RILEGGE invece di aggiornare lo store: cosa il seeding abbia ricreato lo sa il
   * server, e indovinarlo qui vorrebbe dire tenere due verità.
   */
  const handleRestoreTemplates = () => {
    requestConfirm(t('automation.restoreTemplatesConfirm'), async () => {
      setRestoring(true);
      try {
        await dispatch(restoreAutomationTemplates()).unwrap();
        await dispatch(fetchAutomationTemplates()).unwrap();
        toast({ severity: 'success', message: t('automation.restoreTemplatesDone') });
      } catch (err) {
        toast({ severity: 'error', message: t('common.error', { message: String(err) }) });
      } finally {
        setRestoring(false);
      }
    }, {
      requireTypedConfirmation: true,
      // La parola da ridigitare e l'etichetta del pulsante dicono il gesto che si sta per
      // compiere — RIPRISTINA — e non quello di default, che parla di eliminare: qui si
      // elimina per ricreare, e chiedere di scrivere ELIMINA descriverebbe il mezzo invece del
      // fine. Il rosso resta: le versioni derivate se ne vanno davvero.
      typedConfirmationWord: t('automation.restoreTemplatesWord'),
      confirmLabel: t('automation.restoreTemplates'),
    });
  };

  useEffect(() => {
    dispatch(fetchAutomationCategories());
    dispatch(fetchAutomationTemplates());
    // Il catalogo degli oggetti serve al riepilogo di OGNI card (oggetto + evento): una richiesta
    // sola, globale, indipendente da quali template ci siano.
    dispatch(fetchAutomationObjectTypes());
  }, [dispatch]);

  // I campi condizione sono PER ENTITÀ, quindi il catalogo va chiesto una volta per ogni className
  // distinto fra i template a video — non uno per card. La cache dello slice è indicizzata per
  // className, quindi due template sulla stessa entità non fanno due richieste.
  //
  // Il catalogo AZIONI non serve: la card nomina le azioni col loro titolo, che viaggia
  // sull'automazione. Lo si caricava solo per risolvere codice → etichetta, cioè per mostrare il
  // TIPO al posto del nome scelto dall'utente — che era il difetto, non la funzione.
  useEffect(() => {
    for (const className of new Set(templates.map((tmpl) => tmpl.className))) {
      if (className && !conditionTypesByClassName[className])
        dispatch(fetchAutomationConditionTypes(className));
    }
  }, [dispatch, templates, conditionTypesByClassName]);

  // Lookup categoria per id: alimenta nome + colori del chip di ogni card.
  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.automationCategoryId, c])),
    [categories],
  );

  // Riepilogo per template. Finché il catalogo non è arrivato, la resa esce coi segnaposto: le card
  // compaiono subito e si completano, invece di restare vuote ad aspettare.
  const summaryById = useMemo(
    () =>
      new Map(
        templates.map((tmpl) => [
          tmpl.automationDefinitionId,
          summarizeAutomation(
            tmpl,
            { objectTypes, conditionFieldsByClassName: conditionTypesByClassName },
            t,
          ),
        ]),
      ),
    [templates, objectTypes, conditionTypesByClassName, t],
  );

  const filtered = useMemo(
    () =>
      activeCategory === 'all'
        ? templates
        : templates.filter((tmpl) => tmpl.automationCategoryId === activeCategory),
    [templates, activeCategory],
  );

  return (
    <div>
      <Link to="/automations" className="tpl-back">
        <ChevronDownGlyph />
        {t('automation.backToList')}
      </Link>

      {/* Nessuna azione in testata: creare da zero e' gia' la prima tessera della griglia, dove sta
          insieme ai template fra cui si sceglie — cioe' accanto all'alternativa che rappresenta. Un
          pulsante qui sopra diceva la stessa cosa una seconda volta, e piu' lontano dal punto in cui
          la decisione viene presa. */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('automation.title')}</h1>
          <span className="page-sub">{t('automation.galleryLabel')}</span>
        </div>
      </div>

      {/* `--bare`: dentro ci sono gia' le card dei template, una superficie in
          piu' sarebbe solo rumore. */}
      <div className="list-card list-card--bare">
        {/* Filtro categoria a pillole: "tutti" sintetico + categorie reali. */}
        <div className="list-tabs">
          <button
            type="button"
            className={activeCategory === 'all' ? 'on' : undefined}
            aria-pressed={activeCategory === 'all'}
            onClick={() => setActiveCategory('all')}
          >
            {t('automation.categories.all')}
            <span className="cnt">{templates.length}</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.automationCategoryId}
              type="button"
              className={activeCategory === cat.automationCategoryId ? 'on' : undefined}
              aria-pressed={activeCategory === cat.automationCategoryId}
              onClick={() => setActiveCategory(cat.automationCategoryId)}
            >
              {cat.name}
              <span className="cnt">
                {templates.filter((tm) => tm.automationCategoryId === cat.automationCategoryId).length}
              </span>
            </button>
          ))}
        </div>

        <p className="tpl-intro">{t('automation.templatesIntro')}</p>

        <div className="tpl-grid">
          {/* "Crea da zero" e' la prima tessera della griglia, tratteggiata: non
              e' un template ma l'alternativa a sceglierne uno. */}
          <Link className="rcard rcard--new" to="/automations/templates/new">
            <span className="rnew__plus"><PlusGlyph /></span>
            <h3 className="rnew__title">{t('automation.fromScratch.title')}</h3>
            <p className="rnew__sub">{t('automation.fromScratch.subtitle')}</p>
          </Link>

          {filtered.map((template) => {
            const cat = categoryById.get(template.automationCategoryId);
            const summary = summaryById.get(template.automationDefinitionId);
            const statusLabel = t(`automationsInsights.list.statuses.${template.status}`);
            return (
              <article key={template.automationDefinitionId} className="rcard">
                <div className="rcard__head">
                  <span className={`remblem ${emblemTone(template.className)}`}>
                    <AutomationsGlyph />
                  </span>
                  <div>
                    <h3 className="rcard__title">{template.name}</h3>
                    <p className="rcard__desc">{template.description}</p>
                  </div>
                </div>

                {/* Flusso QUANDO/SE/ALLORA del template, dalla stessa
                    `summarizeAutomation` che alimenta il box "Riepilogo" del
                    builder: quello che si legge qui e' quello che si ritrova la'
                    dentro, parola per parola. L'ALLORA resta segnaposto finche'
                    non esiste il catalogo delle azioni.

                    Sul rail i tre passi si distinguono per FORMA oltre che per
                    colore (la condizione e' un rombo): leggibile anche senza
                    percepire la differenza cromatica. */}
                {/* SE e ALLORA compaiono solo se il template li ha davvero: un
                    passo con "..." non dice niente e allunga la card. Il QUANDO
                    resta sempre — un'automazione senza innesco non esiste, e se
                    il catalogo non e' ancora arrivato il segnaposto e' l'unica
                    risposta onesta. Il filo del rail si adatta da solo: lo
                    disegna `:not(:last-child)`, che guarda i passi resi. */}
                <div className="rflow">
                  <div className="rstep">
                    <span className="rstep__rail"><span className="rnode trig" /></span>
                    <div>
                      <div className="rlabel trig">{t('common.when')}</div>
                      <div className="rtext">{summary?.when ?? SUMMARY_PLACEHOLDER}</div>
                    </div>
                  </div>
                  {(summary?.if.length ?? 0) > 0 && (
                    <div className="rstep">
                      <span className="rstep__rail"><span className="rnode cond" /></span>
                      <div>
                        <div className="rlabel cond">{t('common.ifLabel')}</div>
                        <div className="rtext"><AutomationSummaryTokens tokens={summary?.if ?? []} /></div>
                      </div>
                    </div>
                  )}
                  {(summary?.then.length ?? 0) > 0 && (
                    <div className="rstep">
                      <span className="rstep__rail"><span className="rnode act" /></span>
                      <div>
                        <div className="rlabel act">{t('common.thenLabel')}</div>
                        <div className="rtext">{summary?.then.join(' · ')}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rcard__foot">
                  {/* Categoria coi colori decisi lato dati (fontColor/backColor):
                      restano inline perche' arrivano dal record, non dal tema. */}
                  <div className="rmeta">
                    <span
                      className="chip neutral"
                      style={cat ? { color: cat.fontColor, backgroundColor: cat.backColor, borderColor: 'transparent' } : undefined}
                    >
                      {cat?.name ?? template.automationCategoryName ?? ''}
                    </span>
                    <span className={`chip ${STATUS_CHIP_TONE[template.status]}`}>
                      {statusLabel} · v{template.version}
                    </span>
                  </div>
                  {/* "Visualizza template": apre la view SOLA LETTURA del template
                      (/automations/templates/:id), da cui partono le azioni sul
                      template (Modifica/Attiva) e "Usa template". */}
                  <Link
                    className="btn-primary full"
                    to={`/automations/templates/${template.automationDefinitionId}`}
                  >
                    {t('automation.viewTemplate')}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        {/* Empty-state, lo stesso per un sito senza template e per un filtro di categoria vuoto. Al
            sito senza template non si promette il pulsante di ripristino: i predefiniti vengono dai
            plugin cliente abilitati, e dove non ce ne sono il pulsante non crea niente. */}
        {filtered.length === 0 && <p className="tpl-intro">{t('automation.noTemplates')}</p>}
      </div>

      {/* Ripristino dei predefiniti IN FONDO, e fuori dalla card dei template: non è una scelta
          fra template ma manutenzione, e chi sta guardendo la griglia per usarne uno non deve
          incontrarlo sulla strada. Ci si arriva dopo aver scorso tutto, che per un'azione che
          cancella è la distanza giusta — la stessa ragione per cui non sta in testata, dove
          peraltro la pagina non tiene azioni di proposito. */}
      <div className="tpl-maintenance">
        {/* `Trans` e non `t()`: la frase porta un <b> sulla parte che dice cosa si perde, e `t()`
            lo stamperebbe come testo. Il tag sta nella chiave — così ogni lingua decide da sé
            quali parole evidenziare — e qui si dichiara solo con che cosa renderlo. */}
        <p className="tpl-intro">
          <Trans i18nKey="automation.restoreTemplatesHint" components={{ b: <strong /> }} />
        </p>
        <button
          type="button"
          className="btn-danger"
          disabled={restoring}
          onClick={handleRestoreTemplates}
        >
          {t('automation.restoreTemplates')}
        </button>
      </div>

      <ConfirmDialog
        open={isOpen}
        message={message}
        {...options}
        onConfirm={confirm}
        onCancel={cancel}
      />
    </div>
  );
}
